// Shell — the unified-shell layout (UI_SPEC §3.1, revised 2026-05-18).
//
// Top bar (48 px) + horizontal nav strip (40 px) + main canvas + right
// pin pane (320 px when content is present, else hidden so the main
// canvas reclaims the column).
//
// The nav moved from a 200 px left sidebar to a horizontal strip across
// the top to free ~200 px of width on every view — particularly useful for
// the Gantt / dependency-network / heatmap surfaces.

import { type ReactNode } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { ChatPill } from "./ChatPill.js";
import { RightPinPane } from "./RightPinPane.js";
import { TopBar } from "./TopBar.js";
import { ShellProvider, useShell } from "./ShellContext.js";

const NAV_ITEMS: Array<{ to: string; label: string }> = [
  { to: "/",                   label: "Dashboard" },
  { to: "/gantt",              label: "Schedule / Gantt" },
  { to: "/wbs",                label: "Work-breakdown" },
  { to: "/critical-path",      label: "Critical path" },
  { to: "/dependency-network", label: "Dependency network" },
  { to: "/risk",               label: "Risk register" },
  { to: "/earned-value",       label: "Earned value" },
  { to: "/resources",          label: "Resources" },
  { to: "/work-packages",      label: "Work-packages" },
  { to: "/canonical",          label: "Canonical YAML" },
  { to: "/ops",                label: "Health & ops" },
];

export function Shell({ rightPane }: { rightPane?: ReactNode }) {
  return (
    <ShellProvider>
      <div className="flex h-full flex-col">
        <TopBar />
        <TopNav />
        <ShellBody rightPane={rightPane} />
        <ChatPill />
      </div>
    </ShellProvider>
  );
}

function ShellBody({ rightPane }: { rightPane?: ReactNode }) {
  const { viewRightPane, selected, chat } = useShell();
  const hasPinContent =
    viewRightPane != null
    || selected != null
    || (chat.pinboard?.length ?? 0) > 0
    || (chat.sidekick?.length ?? 0) > 0
    || rightPane != null;

  const gridCols = hasPinContent
    ? "grid-cols-[minmax(0,1fr)_320px]"
    : "grid-cols-[minmax(0,1fr)]";

  return (
    <div className={`grid flex-1 overflow-hidden ${gridCols}`}>
      <main className="overflow-auto p-3">
        <Outlet />
      </main>
      {hasPinContent && <RightPinPane>{rightPane}</RightPinPane>}
    </div>
  );
}

function TopNav() {
  return (
    <nav className="flex flex-shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white px-3 py-1.5 text-sm">
      {NAV_ITEMS.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          className={({ isActive }) =>
            "whitespace-nowrap rounded px-2.5 py-1 transition-colors " +
            (isActive
              ? "bg-slate-900 text-white"
              : "text-slate-700 hover:bg-slate-100")
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}
